import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertCircle, CreditCard, FileCheck } from 'lucide-react';
import { BankAccount, PensionConsent } from '@/types/pensions';

interface PensionSetupTodosProps {
  bankAccount: BankAccount | null;
  consent: PensionConsent | null;
  onBankAccountClick: () => void;
  onConsentClick: () => void;
  showPostponementMode?: boolean;
}

interface TodoItem {
  id: string;
  title: string;
  description: string;
  isComplete: boolean;
  isRequired: boolean;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}

export default function PensionSetupTodos({
  bankAccount,
  consent,
  onBankAccountClick,
  onConsentClick,
  showPostponementMode = false,
}: PensionSetupTodosProps) {
  // Define todo items based on current state
  const todoItems: TodoItem[] = [
    {
      id: 'bank-account',
      title: 'Set up employer bank account',
      description: showPostponementMode 
        ? 'Optional during postponement period' 
        : 'Required for pension contributions',
      isComplete: !!bankAccount,
      isRequired: !showPostponementMode,
      icon: CreditCard,
      onClick: onBankAccountClick,
    },
    {
      id: 'consent',
      title: 'Accept consent & terms',
      description: 'Required for pension management',
      isComplete: !!consent,
      isRequired: true,
      icon: FileCheck,
      onClick: onConsentClick,
    },
  ];

  // Filter out completed items (they disappear when done)
  const pendingTodos = todoItems.filter(item => !item.isComplete);
  const completedCount = todoItems.length - pendingTodos.length;
  const totalCount = todoItems.length;

  // If all todos are complete, show a minimal success state
  if (pendingTodos.length === 0) {
    return (
      <Card className="w-full">
        <CardContent className="py-4 px-4 sm:px-6 sm:py-5">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            <span className="text-sm sm:text-base font-medium text-gray-900">
              Setup complete
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-3 pt-4 px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm sm:text-base font-semibold">Setup Tasks</CardTitle>
          <Badge variant="secondary" className="text-xs sm:text-sm">
            {completedCount}/{totalCount}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="px-4 sm:px-6 pb-4 pt-0 space-y-2 sm:space-y-3">
        {pendingTodos.map((todo) => {
          const IconComponent = todo.icon;
          
          return (
            <button
              key={todo.id}
              className="w-full text-left p-3 sm:p-4 hover:bg-gray-50 active:bg-gray-100 rounded-md transition-colors group touch-manipulation"
              onClick={todo.onClick}
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {todo.isRequired ? (
                    <div className="w-2 h-2 rounded-full bg-yellow-500" />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <IconComponent className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                      {todo.title}
                    </p>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1">
                    {todo.description}
                  </p>
                </div>
                <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
          );
        })}
        
        {showPostponementMode && pendingTodos.length > 0 && (
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
            <div className="flex items-start space-x-2 sm:space-x-3">
              <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs sm:text-sm text-blue-600">
                <span className="font-medium">Note:</span> Bank account optional during postponement
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
